import { useEffect, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Convert URL-safe base64 (used by VAPID) → Uint8Array for PushManager.subscribe
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

/**
 * Registers /sw.js, requests notification permission, and subscribes to web push
 * when the user is logged in. Idempotent — safe to mount once at the app shell.
 */
export default function useWebPush(user) {
  const setupRef = useRef(false);

  useEffect(() => {
    if (!user) return;
    if (setupRef.current) return;
    setupRef.current = true;

    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
      return;
    }

    (async () => {
      try {
        // Register SW
        const registration = await navigator.serviceWorker.register('/sw.js');

        // Listen to navigations from the SW
        navigator.serviceWorker.addEventListener('message', (e) => {
          if (e.data?.type === 'navigate' && e.data.url) {
            // Use history navigation if available; otherwise location
            try { window.history.pushState({}, '', e.data.url); window.dispatchEvent(new PopStateEvent('popstate')); }
            catch (_) { window.location.assign(e.data.url); }
          }
        });

        // Permission gate (don't auto-prompt; show prompt only once)
        const seenPrompt = localStorage.getItem('db_push_prompted');
        let permission = Notification.permission;
        if (permission === 'default' && !seenPrompt) {
          localStorage.setItem('db_push_prompted', '1');
          permission = await Notification.requestPermission();
        }
        if (permission !== 'granted') return;

        // Get / create push subscription
        let sub = await registration.pushManager.getSubscription();
        if (!sub) {
          const { data } = await axios.get(`${API}/push/vapid-key`);
          const vapid = data?.vapid_public_key;
          if (!vapid) return;
          sub = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapid),
          });
        }

        // Send to backend
        const token = localStorage.getItem('dharmbook_token');
        if (!token || !sub) return;
        const json = sub.toJSON();
        await axios.post(
          `${API}/push/subscribe`,
          { endpoint: json.endpoint, keys: json.keys },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        // Non-fatal — push is best-effort
        // eslint-disable-next-line no-console
        console.warn('Web push setup skipped:', err?.message || err);
      }
    })();
  }, [user]);
}
